import { useMemo } from 'react'

import type { api } from './api'
import { useSession } from './SessionContext'

/* ============================================================================
   เข้างานนอกสถานที่ — type / ตัวเรียก API / ตัวจัดรูปแบบเวลา
   ใช้ร่วมกันทั้ง 3 หน้าใน src/routes/offsite/
   ตารางจริง: backend/sql/mssql/005_offsite_checkin.sql  (อ่านหัวไฟล์ก่อนแก้ที่นี่)

   ── เรื่องเวลา (จุดที่พลาดง่ายที่สุดในไฟล์นี้) ──────────────────────────────
   เวลาที่เป็น "เรื่องของธุรกิจ" (ช่วงเวลาเปิด, วันเริ่ม/สิ้นสุดสิทธิ์) เป็น **เวลาไทย**
   ทั้งฝั่ง DB และฝั่งนี้ ไม่ใช่ UTC

   ⚠️ ห้ามเอาค่าเหล่านี้เข้า `new Date()` / `.toISOString()` เด็ดขาด
      - ส่งขึ้น: `new Date(x).toISOString()` จะแปลงเป็น UTC ทำให้เวลาเลื่อนไป 7 ชั่วโมง
        (เปิด 09:00 กลายเป็น 02:00) → ส่งเป็น string `YYYY-MM-DDTHH:mm` ตรง ๆ เท่านั้น
      - แสดงผล: ค่าที่ backend ส่งกลับอาจเป็น `2026-08-01T09:00:00` หรือติด `Z`
        มาด้วย (ถ้า driver คืน Date แล้ว JSON.stringify) ซึ่งเป็น **เวลาไทยที่ถูก
        ติดป้ายว่า UTC** ถ้าเอาเข้า new Date() จะเลื่อนอีก 7 ชั่วโมง
        → ตัวจัดรูปแบบข้างล่างจึงอ่านจาก "ข้อความ" ล้วน ไม่สร้าง Date เลย
   ============================================================================ */

export const OFFSITE_BASE = '/api/admin/offsite'

/**
 * ผลลัพธ์ที่แบ่งหน้า — ทุก endpoint ที่คืนรายการใช้รูปแบบนี้
 * total คือจำนวน "ทั้งหมด" ไม่ใช่จำนวนในหน้านี้ (กติกาที่ backend/src/lib/paging.js)
 */
export type Paged<T> = { total: number; page: number; pageSize: number } & T

/** พารามิเตอร์แบ่งหน้าที่ทุกตัวรับเหมือนกัน — ไม่ส่ง = หน้า 1 ขนาดตามกติกา (10) */
export type PageOpts = { page?: number; pageSize?: number; sort?: string }

export type OffsiteMode = 'head_office' | 'anywhere'

export const MODE_LABEL: Record<OffsiteMode, string> = {
  head_office: 'ในรัศมีสถานที่',
  anywhere: 'จากที่ใดก็ได้',
}

/**
 * ⚠️ lat/lng เป็น DECIMAL(9,6) และ isHeadOffice เป็น BIT ใน MSSQL — driver ส่งกลับมา
 * เป็น string / 0-1 ได้ (backend เองก็ยัง Number(site.lat) อยู่) จึงรับไว้กว้าง ๆ
 * แล้วแปลงตอนใช้: พิกัดผ่าน coordText() · ธงผ่าน Boolean() ห้ามเทียบ `=== true`
 */
export type OffsiteSite = {
  id: number
  code: string
  name: string
  lat: number | string
  lng: number | string
  radiusM: number
  isHeadOffice: boolean | number
  status: 'active' | 'disabled'
  note: string | null
  createdBy?: string | null
  updatedBy?: string | null
}

export type OffsiteEmployee = {
  empCode: string
  empName: string | null
  department: string | null
  position: string | null
  /** มีใบอนุญาตที่ยังใช้ได้อยู่แล้ว — ออกใบใหม่ให้คนนี้จะชน 409 ถ้าไม่ส่ง replace */
  hasPermission: boolean
}

/**
 * สถานที่หนึ่งแห่งที่ใบอนุญาตผูกไว้ (มาจากตาราง line_checkin_permission_site)
 *
 * มีแค่ 4 ฟิลด์พอสำหรับแสดงผล — รัศมี/พิกัดต้องไปหาเอาจาก listSites()
 * ⚠️ `status` อาจเป็น 'disabled' ได้ (สถานที่ถูกปิดหลังออกใบ) ต้องยังแสดงให้เห็น
 *    ห้ามกรองทิ้งเงียบ ๆ ไม่งั้นคนกดบันทึกแล้วสิทธิ์นั้นหลุดไปโดยไม่รู้ตัว
 */
export type OffsitePermissionSite = {
  id: number
  code: string
  name: string
  status: 'active' | 'disabled'
}

export type OffsitePermission = {
  id: number
  empCode: string
  empName: string | null
  mode: OffsiteMode
  /**
   * สถานที่ที่ใบนี้อนุญาต — **ระบุไว้เท่านั้น ไม่มีความหมายว่า "ทุกสถานที่"**
   * (ดูเหตุผลที่ตัดทิ้งใน backend/sql/mssql/006_permission_sites.sql)
   *   mode = 'anywhere'    → [] เสมอ (ไม่ตรวจพิกัดอยู่แล้ว)
   *   mode = 'head_office' → ปกติมีอย่างน้อย 1 แต่ **เป็น [] ได้** ถ้าเป็นข้อมูลที่ตั้งค่าค้างไว้
   *                          ซึ่งแปลว่าคนนั้นลงเวลาไม่ได้เลย → ตารางต้องเตือนให้เห็น
   */
  sites: OffsitePermissionSite[]
  effectiveFrom: string | null
  effectiveTo: string | null
  status: 'active' | 'revoked'
  note: string | null
  createdBy: string | null
  /**
   * ⚠️ created_at/revoked_at เก็บเป็น **UTC** (ร่องรอยระบบ) ต่างจาก effective_from/to
   * ที่เป็นเวลาไทย — และสัญญา API ยังไม่ได้ระบุว่าส่งกลับมาติด Z หรือไม่
   * จึงยังไม่เอาไปแสดงบนหน้าจอ (แปลงผิดทางไหนก็คลาด 7 ชั่วโมงแบบเงียบ ๆ)
   */
  createdAt?: string | null
  revokedAt?: string | null
  revokeReason?: string | null
}

export type OffsiteWindow = {
  id: number
  /** เวลาไทยในรูป `YYYY-MM-DDTHH:mm:ss` — ดูคำเตือนหัวไฟล์ก่อนนำไปคำนวณ */
  startsAt: string
  endsAt: string
  siteId: number | null
  reason: string
  status: 'active' | 'cancelled'
  createdBy: string | null
  /** UTC เหมือน created_at ของใบอนุญาต — ยังไม่เอาไปแสดง (ดูคำอธิบายใน OffsitePermission) */
  cancelledAt?: string | null
  cancelledBy?: string | null
  cancelReason?: string | null
}

export type SitePayload = {
  code: string
  name: string
  lat: number
  lng: number
  radiusM: number
  isHeadOffice: boolean
  note: string | null
}

export type PermissionPayload = {
  empCode: string
  mode: OffsiteMode
  /**
   * รายการ id สถานที่ที่อนุญาต — backend บังคับว่า
   *   mode = 'head_office' → ต้องมีอย่างน้อย 1 (ว่าง = 400)
   *   mode = 'anywhere'    → ต้องเป็น [] (ส่งมาไม่ว่าง = 400)
   * ⚠️ ไม่มีค่าที่แปลว่า "ทุกสถานที่" — ปุ่ม "เลือกทั้งหมด" บนหน้าเว็บติ๊กสถานที่ที่มี
   *    ณ ตอนนั้นให้เป็นรายการจริง เพื่อไม่ให้สิทธิ์ขยายเองเมื่อเปิดสาขาใหม่
   */
  siteIds: number[]
  /** `YYYY-MM-DD` (คอลัมน์เป็น DATE ไม่มีเวลา จึงไม่มีเรื่อง timezone) */
  effectiveFrom: string | null
  effectiveTo: string | null
  note: string | null
  /** ส่งเฉพาะรอบที่ผู้ใช้ยืนยันแล้วว่าจะทับใบเดิม — ห้ามส่งตั้งแต่ครั้งแรก */
  replace?: true
}

/**
 * ฟิลด์ที่แก้ได้ของใบที่ "ยังใช้งานอยู่"
 *
 * ไม่มี empCode โดยตั้งใจ — เปลี่ยนตัวคนในใบเดิมไม่ได้ นั่นคือใบของอีกคนหนึ่ง
 * ต้องออกใบใหม่ ไม่งั้นประวัติจะเล่าว่าคนนี้เคยมีสิทธิ์มาตั้งแต่วันที่ออกใบให้อีกคน
 *
 * ⚠️ ใบที่ถูกยกเลิกแล้วแก้ไม่ได้ backend ตอบ 409 (ดู lib/offsite.js updatePermission)
 */
export type PermissionEditPayload = Omit<PermissionPayload, 'empCode' | 'replace'>

export type WindowPayload = {
  /** `YYYY-MM-DDTHH:mm` เวลาไทย (ค่าที่ DateTimePicker ส่งออกมา) */
  startsAt: string
  endsAt: string
  siteId: number | null
  reason: string
}

/* ───────────────────────── ตัวช่วยแสดงผล ───────────────────────── */

export function errorText(err: unknown): string {
  return err instanceof Error ? err.message : String(err)
}

/** พิกัดโชว์ 6 ตำแหน่งให้ตรงกับที่ DB เก็บได้จริง (DECIMAL(9,6)) */
export function coordText(v: number | string): string {
  const n = Number(v)
  return Number.isFinite(n) ? n.toFixed(6) : String(v)
}

/** ลิงก์ตรวจพิกัด — คนตั้งค่าควรเปิดดูว่าหมุดลงตรงตัวอาคารจริงก่อนบันทึก */
export function mapsUrl(lat: number | string, lng: number | string): string {
  return `https://www.google.com/maps?q=${coordText(lat)},${coordText(lng)}`
}

/**
 * ตัดให้เหลือ `YYYY-MM-DDTHH:mm` เพื่อ**เทียบเวลาด้วยการเทียบข้อความ**
 * รูปแบบนี้เรียงตามตัวอักษรแล้วได้ลำดับเวลาพอดี จึงไม่ต้องสร้าง Date (ดูหัวไฟล์)
 */
export function minuteKey(raw: string | null | undefined): string {
  if (!raw) return ''
  return raw.replace(' ', 'T').slice(0, 16)
}

export type WindowState = 'open' | 'upcoming' | 'expired' | 'cancelled'

export const WINDOW_STATE_LABEL: Record<WindowState, string> = {
  open: 'กำลังเปิด',
  upcoming: 'รอเริ่ม',
  expired: 'หมดเวลาแล้ว',
  cancelled: 'ยกเลิกแล้ว',
}

/**
 * สถานะของช่วงเวลาไว้ **แสดงในตารางเท่านั้น** — คำตอบที่เชื่อถือได้ว่า
 * "ตอนนี้เปิดอยู่ไหม" คือ `current` ที่ backend คำนวณจากนาฬิกา DB
 * (หน้าเว็บอาจเปิดค้างไว้ข้ามวัน หรือนาฬิกาเครื่องไม่ตรง)
 */
export function windowState(w: OffsiteWindow, nowKey: string): WindowState {
  if (w.status === 'cancelled') return 'cancelled'
  if (minuteKey(w.endsAt) < nowKey) return 'expired'
  if (minuteKey(w.startsAt) > nowKey) return 'upcoming'
  return 'open'
}

/* ───────────────────────── ตัวเรียก API ───────────────────────── */

function query(params: Record<string, string | undefined>): string {
  const qs = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== '') qs.set(key, value)
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

/**
 * แกะ array ออกจากผลลัพธ์แบบทนทาน — สัญญาบอกว่าห่อไว้ในคีย์ (`{ sites: [...] }`)
 * แต่ backend ทำพร้อมกันกับหน้านี้ ถ้าตอบเป็น array เปล่ามาก็ยังโชว์ได้
 * แทนที่จะพังทั้งหน้าเพราะ `.map` ของ undefined
 */
function listOf<T>(res: unknown, key: string): T[] {
  if (Array.isArray(res)) return res as T[]
  const value = res && typeof res === 'object' ? (res as Record<string, unknown>)[key] : undefined
  return Array.isArray(value) ? (value as T[]) : []
}

const json = (body: unknown): RequestInit => ({ body: JSON.stringify(body) })

/**
 * บังคับให้ `sites` เป็น array เสมอ — ทั้งหน้าอ่านค่านี้ด้วย `.map`/`.length`
 * ถ้า backend (ที่ทำพร้อมกันอยู่) ยังไม่ส่งคีย์นี้มา หน้าจะขาวทั้งหน้าแทนที่จะโชว์ตาราง
 * ⚠️ [] ไม่ใช่แค่ค่ากันพัง — เป็นสถานะจริงที่ต้องรองรับ (ใบ head_office ที่ยังไม่ระบุสถานที่
 *    = ลงเวลาไม่ได้เลย) ตารางจึงต้องเตือน ไม่ใช่ปล่อยให้ดูเหมือนใบปกติ
 */
function normalizePermissions(rows: OffsitePermission[]): OffsitePermission[] {
  return rows.map((p) => (Array.isArray(p.sites) ? p : { ...p, sites: [] }))
}

export function offsiteApi(call: typeof api) {
  return {
    /* ── สถานที่ ── */
    listSites: () =>
      call<unknown>(`${OFFSITE_BASE}/sites`).then((res) => listOf<OffsiteSite>(res, 'sites')),

    createSite: (body: SitePayload) =>
      call<unknown>(`${OFFSITE_BASE}/sites`, { method: 'POST', ...json(body) }),

    // code แก้ไม่ได้หลังสร้าง (ดูเหตุผลใน SiteForm) จึงไม่ส่งขึ้นมาด้วย
    updateSite: (id: number, body: Omit<SitePayload, 'code'>) =>
      call<unknown>(`${OFFSITE_BASE}/sites/${id}`, { method: 'PATCH', ...json(body) }),

    setSiteEnabled: (id: number, enabled: boolean) =>
      call<unknown>(`${OFFSITE_BASE}/sites/${id}/${enabled ? 'enable' : 'disable'}`, {
        method: 'POST',
      }),

    /* ── ใบอนุญาตรายคน ── */
    listPermissions: (opts: { status: 'active' | 'all'; q?: string } & PageOpts) =>
      call<Paged<{ permissions?: OffsitePermission[] }>>(
        `${OFFSITE_BASE}/permissions${query({
          status: opts.status,
          q: opts.q,
          page: opts.page === undefined ? undefined : String(opts.page),
          pageSize: opts.pageSize === undefined ? undefined : String(opts.pageSize),
          sort: opts.sort,
        })}`,
      ).then((res) => ({
        permissions: normalizePermissions(listOf<OffsitePermission>(res, 'permissions')),
        total: res?.total ?? 0,
        page: res?.page ?? 1,
        pageSize: res?.pageSize ?? 10,
      })),

    createPermission: (body: PermissionPayload) =>
      call<unknown>(`${OFFSITE_BASE}/permissions`, { method: 'POST', ...json(body) }),

    // แก้ได้เฉพาะใบที่ยัง active — ใบที่ยกเลิกแล้ว backend ตอบ 409 ให้ไปออกใบใหม่แทน
    updatePermission: (id: number, body: PermissionEditPayload) =>
      call<unknown>(`${OFFSITE_BASE}/permissions/${id}`, { method: 'PATCH', ...json(body) }),

    revokePermission: (id: number, reason: string) =>
      call<unknown>(`${OFFSITE_BASE}/permissions/${id}/revoke`, { method: 'POST', ...json({ reason }) }),

    /* ── ช่วงเวลาเปิดให้ทุกคน ── */
    listWindows: (status: 'active' | 'all', opts: PageOpts = {}) =>
      call<Paged<{ windows?: OffsiteWindow[]; current?: OffsiteWindow | null }>>(
        `${OFFSITE_BASE}/windows${query({
          status,
          page: opts.page === undefined ? undefined : String(opts.page),
          pageSize: opts.pageSize === undefined ? undefined : String(opts.pageSize),
          sort: opts.sort,
        })}`,
      ).then((res) => ({
        windows: Array.isArray(res?.windows) ? res.windows : [],
        // current มาจาก backend (ถามจาก checkinPolicy) ไม่ได้หาจากแถวในหน้านี้
        // — ช่วงเวลาที่เปิดอยู่อาจไม่ได้อยู่หน้าแรก ดูเหตุผลใน lib/offsite.js
        current: res?.current ?? null,
        total: res?.total ?? 0,
        page: res?.page ?? 1,
        pageSize: res?.pageSize ?? 10,
      })),

    createWindow: (body: WindowPayload) =>
      call<unknown>(`${OFFSITE_BASE}/windows`, { method: 'POST', ...json(body) }),

    cancelWindow: (id: number, reason: string) =>
      call<unknown>(`${OFFSITE_BASE}/windows/${id}/cancel`, { method: 'POST', ...json({ reason }) }),

    /* ── ค้นหาพนักงาน (ไว้เลือกตอนออกใบ ห้ามให้พิมพ์รหัสมือเปล่า) ── */
    searchEmployees: (q: string) =>
      call<unknown>(`${OFFSITE_BASE}/employees${query({ q })}`).then((res) =>
        listOf<OffsiteEmployee>(res, 'employees'),
      ),
  }
}

export type OffsiteApi = ReturnType<typeof offsiteApi>

/**
 * ตัวเรียก API ของฟีเจอร์นี้ — ผูกกับ `call()` ของ SessionContext เสมอ
 * (ไม่ใช่ `api()` ตรง ๆ) เพื่อให้ 401 ระหว่างใช้งานเคลียร์ session ที่เดียว
 */
export function useOffsite(): OffsiteApi {
  const { call } = useSession()
  return useMemo(() => offsiteApi(call), [call])
}
